<template>
  <sfs-form-modal
    :id="modalId"
    title="Add a Recurring Charge"
    @on-hide="() => recurringCharge = null"
  >
    <b-form
      novalidate
      @submit.stop.prevent="handleSubmit"
    >
      <b-row>
        <b-col>
          <b-alert
            :show="hasBaseServerErrors()"
            variant="danger"
            dismissible
          >
            <p
              class="mb-0"
              v-for="(error, index) in baseServerErrors"
              :key="index"
            >
              {{ error }}
            </p>
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
        >
          <b-form-group
            label="Period to bill for"
            label-for="invoice-item-add-recurring-period"
          >
            <sfs-date-range-picker
              id="invoice-item-add-recurring-period"
              v-model="$v.form.period.$model"
              :with-class="{ 'is-invalid': ($v.form.start_date.$dirty && $v.form.start_date.$error) || ($v.form.end_date.$dirty && $v.form.end_date.$error) }"
              :class="{ 'is-invalid': ($v.form.start_date.$dirty && $v.form.start_date.$error) || ($v.form.end_date.$dirty && $v.form.end_date.$error) }"
              placeholder="Select Period"
              @on-change="onPeriodChange"
            />
            <b-form-invalid-feedback
              v-if="$v.form.start_date.$dirty"
              id="invoice-item-add-recurring-start_date-feedback"
            >
              <span v-if="!$v.form.start_date.required">Please enter a start date.</span>
              <span v-if="!$v.form.start_date.serverFailed">{{ serverErrors.start_date }}</span>
            </b-form-invalid-feedback>
            <b-form-invalid-feedback
              v-if="$v.form.end_date.$dirty"
              id="invoice-item-add-recurring-end_date-feedback"
            >
              <span v-if="!$v.form.end_date.required">Please enter an end date.</span>
              <span v-if="!$v.form.end_date.serverFailed">{{ serverErrors.end_date }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col>
          <b-form-group
            label="Recurring Charge"
            label-for="invoice-item-add-recurring-recurring_charge_id"
          >
            <recurring-charges-select
              id="invoice-item-add-recurring-recurring_charge_id"
              :value="recurringCharge"
              :select-class="{ 'is-invalid': $v.form.recurring_charge_id.$dirty && $v.form.recurring_charge_id.$error }"
              :filter-params="recurringChargeSelectParams"
              placeholder="Search by store number"
              @input="onRecurringChargeChange"
            />
            <b-form-invalid-feedback
              v-if="$v.form.recurring_charge_id.$dirty"
              id="invoice-item-add-recurring-recurring_charge_id-feedback"
            >
              <span v-if="!$v.form.recurring_charge_id.required">Please enter a charge.</span>
              <span v-if="!$v.form.recurring_charge_id.serverFailed">{{ serverErrors.recurring_charge_id }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <hr class="mt-4">

      <b-row>
        <b-col
          offset-lg="6"
          lg="3"
          order="2"
          order-lg="1"
        >
          <b-button
            variant="link-dark"
            block
            id="invoice-item-add-recurring-close-button"
            @click="closeModal"
          >
            Cancel
          </b-button>
        </b-col>
        <b-col
          lg="3"
          order="1"
          order-lg="2"
        >
          <b-button
            id="invoice-item-add-recurring-submit-button"
            block

            type="submit"
            variant="primary"
            :disabled="processing"
          >
            Save
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </sfs-form-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import formMixin from '@/mixins/formMixin';
import formModalMixin from '@/mixins/formModalMixin';
import RecurringChargesSelect from '@/components/admin/recurringCharges/Select';

export default {
  name: 'InvoicesAddRecurringChargeModal',
  mixins: [formMixin, formModalMixin],
  components: {
    RecurringChargesSelect,
  },
  props: {
    invoice_id: { type: Number, required: true },
    client_id: { type: Number, required: true },
    recurring_charge_id: Number,
    start_date: String,
    end_date: String,
  },
  data() {
    return {
      form: {
        recurring_charge_id: this.recurring_charge_id,
        start_date: this.start_date,
        end_date: this.end_date,
        period: [
          this.start_date && this.$options.filters.dateFormat(this.start_date),
          this.end_date && this.$options.filters.dateFormat(this.end_date),
        ],
      },
      recurringCharge: null,
      recurringChargeSelectParams: {
        client_id: this.client_id,
        start_date: this.start_date,
        end_date: this.end_date,
        active: true,
      },
      modalId: 'invoice-item-add-recurring-charge-modal',
    };
  },
  validations: {
    form: {
      recurring_charge_id: {
        required,
        serverFailed() {
          return !this.hasServerErrors('recurring_charge_id');
        },
      },
      period: {},
      start_date: {
        required,
        serverFailed() {
          return !this.hasServerErrors('start_date');
        },
      },
      end_date: {
        required,
        serverFailed() {
          return !this.hasServerErrors('end_date');
        },
      },
    },
  },
  methods: {
    onRecurringChargeChange(recurringCharge) {
      this.form.recurring_charge_id = recurringCharge ? recurringCharge.id : '';
      this.recurringCharge = recurringCharge;
    },
    onPeriodChange(dates) {
      this.form.start_date = dates[0] || '';
      this.form.end_date = dates[1] || '';
      this.recurringChargeSelectParams.start_date = dates[0] || '';
      this.recurringChargeSelectParams.end_date = dates[1] || '';
    },
    handleSubmit() {
      if (this.hasFormErrors()) {
        return;
      }
      this.processing = true;
      this.$invoicesAPI
        .createRecurringChargeItem(this.invoice_id, this.form)
        .then(() => {
          this.resetServerErrors();
          this.$v.$reset();
          this.$emit('on-save');
          this.closeModal();
          this.processing = false;
        })
        .catch((error) => {
          this.processServerErrors(error.response.data.errors);
          this.processing = false;
        });
    },
  },
};
</script>
